import { MarketplaceItemV2, ProcessProviderId } from '@fgb/core';
import { MarketplaceButtonAction } from './marketplace-button.action';
import { RedeemModalButtonAction } from './redeem-modal-button.action';
import { MerchantButtonAction } from './merchant-button.action';

export interface MarketplaceActionRule {
  readonly when: (item: MarketplaceItemV2) => boolean;
  readonly create: () => MarketplaceButtonAction;
  readonly label: (id: ProcessProviderId) => string;
}

/** Helper function to get the button label based on the process provider ID */
export function getButtonLabel(providerId: ProcessProviderId): string {
  switch (providerId) {
    case ProcessProviderId.Event:
      return 'marketplace.redeem.event';
    case ProcessProviderId.VirtualEvent:
      return 'marketplace.redeem.virtual.event';
    case ProcessProviderId.Product:
      return 'marketplace.redeem.product';
    default:
      return 'marketplace.redeem.marketplace';
  }
}

export function isMerchantItem(item: MarketplaceItemV2): boolean {
  return !!(item.ShippingEnabled || (item.MerchantValue ?? 0) > 0);
}

/**
 * This array defines the rules for determining which action button to display for a marketplace item based on its properties.
 * Each rule consists of a `when` function that checks specific conditions on the item, a `create` function that returns an instance of the corresponding action, and a `label` function that provides the appropriate label for the button based on the process provider ID.
 * The first rule checks if the item has shipping enabled or a merchant value greater than 0, which indicates that it can be redeemed, and assigns the `RedeemModalButtonAction` to it. The label is determined by the process provider ID to provide context-specific text for the button.
 * Additional rules can be added to this array to handle different types of items and actions as needed.
 *
 * This separates action-selection logic from the main component, making action replacement and extension possible without modifying marketplace details component code.
 */
export const actionRules: Array<MarketplaceActionRule> = [
  // Points or Ecash Item
  {
    when: (item) => !isMerchantItem(item),
    create: () => new RedeemModalButtonAction(),
    label: (id) => getButtonLabel(id),
  },
  // Merchant Item
  {
    when: (item) => isMerchantItem(item),
    create: () => new MerchantButtonAction(),
    label: (id) => getButtonLabel(id),
  },
  // Add more item-specific rules here.
];
